import { Interval, Note } from 'tonal';
import { MAX_FRET, chromaOf, chromaToPc, type AccidentalPref } from './fretboard';

/**
 * Harmony theory (topics C8, C9, C10, I3): diatonic chords, Roman and
 * Nashville numerals, the progression catalogue, capo and transposition maths,
 * and the circle of fifths.
 *
 * Conventions:
 * - Minor means natural minor (Aeolian).
 * - Numerals are measured against the major scale on the same tonic, so a
 *   minor key reads i ii° bIII iv v bVI bVII (research/03 C9 writes
 *   i–bVII–bVI–bVII). Nashville numbers follow the same rule: 1m 2° b3 4m 5m
 *   b6 b7, with the minor tonic as 1.
 * - Chord roots are spelled by key: F# in D major, Bb for bVII in C.
 */

export type KeyMode = 'major' | 'minor';

export interface MusicalKey {
  /** Pitch class, e.g. `F#`, `Bb`. */
  readonly tonic: string;
  readonly mode: KeyMode;
}

export type ScaleDegree = 1 | 2 | 3 | 4 | 5 | 6 | 7;

export type ChordQuality =
  | 'major'
  | 'minor'
  | 'diminished'
  | 'augmented'
  | 'dominant7'
  | 'major7'
  | 'minor7'
  | 'half-diminished7'
  | 'diminished7';

export interface ChordSpec {
  readonly root: string;
  readonly quality: ChordQuality;
}

export interface DiatonicChord extends ChordSpec {
  readonly degree: ScaleDegree;
  /** Chord symbol, e.g. `F#m`, `Bdim`, `G7`. */
  readonly symbol: string;
  /** Roman numeral, e.g. `vi`, `vii°`, `bVII`, `V7`. */
  readonly roman: string;
  /** Nashville number, e.g. `6m`, `7°`, `b7`, `57`. */
  readonly nashville: string;
}

export interface DiatonicOptions {
  /** Diatonic seventh chords instead of triads. */
  readonly sevenths?: boolean;
}

// ---------------------------------------------------------------------------
// Keys and spelling

                                                                                            
                                                              
                                                                                  
                                                                                  
/** Semitones above the tonic → interval used to spell a root in a key. */
                          
       
       
       
       
       
       
       
       
       
       
       
       
           

                                   
                              
 

/** Validates a pitch class (no octave) and returns it in tonal's spelling. */
                                   
                           
                                                                                      
              
 

/** Collapse double accidentals (Ebb → D) and leave everything else alone. */
                                     
                                                         
 

                                                              
                                              
 

                                                     
                                             
 

                                                     
                                             
 

/** `Am`, `F#m`, `A minor`, `Bb`, `C major` → key. Throws on anything else. */
                                                     
                                                                                           
                                                   
                                                                                     
                                                                   
                                                   
 

/** Compact label used in item IDs and charts: `D`, `Am`. */
                                                   
                                                            
 

/** Spoken name: `D major`, `A minor`. */
                                                  
                                    
 

                                                                
                                                                      
 

/** The seven scale notes of a key, tonic first. */
                                                    
                                                                                         
                                                            
 

/**
 * The note an interval above the tonic, spelled for the key: a note in the
 * key's scale keeps the scale's spelling; any other note keeps the interval's
 * spelling, with double accidentals simplified (Ebb → D).
 */
                                                                
                                                   
                                                                                
 

/**
 * Spell a chromatic pitch class inside a key: diatonic notes use the key's
 * spelling; chromatic ones are flattened degrees (bIII, bVI, bVII), except the
 * tritone, which is #IV.
 */
                                                                     
                                                     
                                                             
 

/**
 * The usual chart spelling of a chord root with no key context: majors as the
 * circle's major keys (Db, Eb, F#, Ab, Bb), minors as its minor keys (C#m,
 * D#m, F#m, G#m, Bbm).
 */
                                                                                  
                                                                              
 

// ---------------------------------------------------------------------------
// Chords

                                                                    
                            
                            
                                 
                                
                                      
                                   
                                   
                                               
                                        
  

                                                     
            
             
                    
                   
                 
                 
               
                             
                      
  

                                                      
              
             
               
             
               
               
                    
                    
                  
                   
                   
                   
                 
               
              
               
               
                 
                 
                           
                        
                         
                      
                      
                    
  

                                                                                

                                                           
                                                 
 

/** True for qualities with a minor third (minor, diminished and their sevenths). */
                                                               
                                                
 

                                                       
                                                            
 

/** `F#m`, `Bdim`, `B°`, `G7`, `Cmaj7`, `Bm7b5`, `Bø` → chord. Throws on anything else. */
                                                             
                                                                  
                                                                 
                                                                                      
                                               
 

                                                       
                                                                     
 

/** Chord tones as pitch classes, root first, e.g. Bm7b5 → B D F A. */
                                                                 
                          
                                                                                
 

                                                                                  
                      
                      
                                                                          
 

// ---------------------------------------------------------------------------
// Numerals

export interface Numeral {
  readonly degree: ScaleDegree;
  /** Semitones relative to the major-scale degree on the tonic: -1 = b, +1 = #. */
  readonly alteration: number;
  readonly quality: ChordQuality;
}

                                                                  

/** Suffix after an upper-case (major-third) or lower-case (minor-third) numeral. */
                                                    
            
            
                  
                 
                 
                 
              
                           
                    
  

                                                        
            
             
                  
                 
                 
                 
               
                           
                    
  

                                                       
                                                                           
 

                                                               
                        
                                                                 
 

                                                  
                                             
                                                                       
                                                                                 
 

                                                     
                                                                                      
 

                                                                                     

/**
 * `vi`, `bVII`, `vii°`, `V7`, `ii7`, `Imaj7`, `viiø7` → numeral; `null` if the
 * text is not a numeral this module writes (case and suffix must agree:
 * `I°` and `vi+` are rejected). `o` is accepted for `°`, and `ø` for `ø7`.
 */
                                                          
                                       
                      
                                 
                                                  
                                                                          
                                       
                                                                    
    
                                         
                                                                                  
                      
                                                                 
 

/** `6m`, `b7`, `7°`, `57`, `2m7`, `1maj7` → numeral; `null` on anything else. */
                                                              
                                                              
                      
                                                                          
                                                                              
                                         
          
                                        
                                       
            
    
 

                                                     
                                                     
                                                              
           
 

/** Interval from the tonic to an altered major-scale degree: (7, -1) → 7m, (4, +1) → 4A. */
                                                                          
                                                               
                                                                                            
                                                                     
                 
                                                                                                
                               
 

/** The chord a numeral names in a key, e.g. (`bVII`, C major) → Bb. */
                                                                                             
                                                                                 
                                                                       
                           
 

function describe(root: string, n: Numeral): DiatonicChord {
  return {
    root,
    quality: n.quality,
    degree: n.degree,
    symbol: chordSymbol({ root, quality: n.quality }),
    roman: romanNumeral(n),
    nashville: nashvilleNumber(n),
  };
}

/**
 * The numeral of a chord in a key, by spelling: Bb in C is bVII, A# in C is
 * #VI. Accepts any root, diatonic or not.
 */
                                                                                
                          
                                                                          
                                                          
                                          
                                                                         
                                       
                                        
                                                    
 

                                                                             
                                             
 

                                                                                 
                                                
 

/** Convert between numeral systems: `vi` ↔ `6m`, `bVII` ↔ `b7`, `vii°` ↔ `7°`. */
                                                         
                              
                                                                     
                            
 

                                                             
                                      
                                                                            
                         
 

// ---------------------------------------------------------------------------
// Diatonic harmony

                                                                                            
          
                                                                                 
                                                                                                  
    
          
                                                                                 
                                                                                                  
    
  

/**
 * The harmonised scale: I ii iii IV V vi vii° in major, i ii° bIII iv v bVI
 * bVII in (natural) minor; or the seventh chords with `sevenths`.
 */
                                                                                              
                                                                                        
                                        
                                          
                                                 
                                                
                                            
     
 

                              
                  
                      
                             
                  
                                                                
 

/** True if the chord (root and quality) is one of the key's triads or sevenths. */
                                                                                 
                          
                                                                       
                                           
 

// ---------------------------------------------------------------------------
// Progressions

export interface Progression {
  readonly id: string;
  readonly name: string;
  readonly mode: KeyMode;
  /** Roman numerals, one per slot (one per bar for the blues). */
  readonly numerals: readonly string[];
  /** A short, learner-facing note on where it is heard. */
  readonly description: string;
}

/** The C9 catalogue (research/03). */
                                                     
   
                 
                   
                  
                               
                                                                                
    
   
                    
                      
                  
                                     
                                                                     
    
   
                    
                      
                  
                                     
                                                                            
    
   
                    
                      
                  
                                     
                                                  
    
   
                 
                   
                  
                               
                                                             
    
   
                       
                         
                  
                                                                                          
                                                                             
    
   
                                    
                                        
                  
                                                                                           
                                                                    
    
   
                          
                            
                  
                                           
                                                                         
    
  

                                                         
                                                  
                                                                     
           
 

/** Rotate left by `n`: rotate([I, V, vi, IV], 2) → [vi, IV, I, V]. */
                                                                
                                    
                                 
                                                   
 

                                            
                           
 

                                                                            
                                                                
 

/** Every distinct rotation, starting with the sequence itself. */
                                                                      
                             
                                             
                                  
                                                          
   
             
 

/**
 * The smallest left rotation turning `a` into `b`, or `null`. 0 means the same
 * sequence. (I–V–vi–IV → vi–IV–I–V is 2.)
 */
                                                                                           
                                         
                               
                                                                                 
              
 

                                                                                   
                                       
 

/** Other catalogue progressions that are rotations of this one (the C9 misconception). */
                                                                                    
                                                                                        
                             
                                                                                      
    
 

export interface ProgressionMatch {
  readonly progression: Progression;
  /** 0 for an exact match; otherwise the left rotation of the catalogue entry. */
  readonly rotation: number;
}

/**
 * Catalogue entries matching a numeral sequence, exact matches first, then
 * rotations. Numerals are compared after normalising (`6m` → `vi`).
 */
                                                                                   
                                                                         
                                     
                                 
                                                      
                                                                  
   
                                                                                 
 

/** Identify a chord chart in a key: [C, G, Am, F] in C → I–V–vi–IV. */
                                    
                  
                                          
                       
                                                              
 

/** Realise numerals (or a catalogue progression) as chords in a key. */
                                  
                  
                                               
                    
                                                                                  
                                                       
 

// ---------------------------------------------------------------------------
// Transposition

/** Semitones up (0–11) from one pitch class or key to another. */
                                                                                         
                                                         
                                                   
                                          
 

/** Transpose a key, spelled the way the circle of fifths names it (A# → Bb). */
                                                                              
          
                                                                       
                   
    
 

/**
 * Transpose a chord by semitones. The new root is spelled in `spelling` when it
 * is a key, with that accidental preference when it is one, and otherwise the
 * usual chart spelling (`standardChordRoot`).
 */
                               
                            
                    
                                         
              
                          
                                              
              
                          
                                            
                                    
                                      
                                       
                                      
 

/**
 * Transpose a chord chart between keys by function, so each chord keeps its
 * numeral and is spelled in the new key: [C, G, Am, F] from C to D → [D, A, Bm, G].
 */
                                
                                          
                   
                 
                    
                                                                     
 

// ---------------------------------------------------------------------------
// Capo

/** Keys whose open-position shapes beginners use under a capo. */
                                                       
                                             
                                   
  

                                         
                                                               
                                                                       
   
 

/**
 * The capo fret that makes `shape` sound as `sounding` (0–11): a song in Bb
 * with G shapes needs capo 3. The capo always transposes up.
 */
                                                                                             
                                      
 

/** The key that sounds when playing in `shape` with a capo: G shapes, capo 3 → Bb. */
                                                                          
                   
                                   
 

/** The shapes to play for a sounding key with a capo: Bb, capo 3 → G shapes. */
                                                                          
                   
                                       
 

/** The chord that sounds from a shape under a capo: G shape, capo 3 → Bb. */
                              
                            
               
                   
              
                   
                                          
 

/** The shape to finger for a sounding chord under a capo: Bb, capo 3 → G shape. */
                           
                               
               
                   
              
                   
                                              
 

export interface CapoOption {
  readonly shape: MusicalKey;
  readonly capo: number;
}

/**
 * Open-shape keys (same mode) that reach `sounding` with a capo at or below
 * `maxCapo` (default 7), lowest capo first.
 */
                            
                       
                                                                  
                 
                                    
                                                
               
                                            
                                                                 
                                     
                                     
 

// ---------------------------------------------------------------------------
// Circle of fifths and key signatures

export interface CircleEntry {
  /** 0 = C at the top, increasing clockwise (towards the sharps). */
  readonly position: number;
  readonly major: string;
  readonly minor: string;
  /** Sharps (+) or flats (−) in the signature of the primary spelling. */
  readonly alteration: number;
  /** The enharmonic spelling at the bottom of the circle (B/Cb, F#/Gb, Db/C#). */
  readonly enharmonic?: {
    readonly major: string;
    readonly minor: string;
    readonly alteration: number;
  };
}

                                                         
                                                         
                                                         
                                                         
                                                          
                                                          
   
                
               
                
                  
                                                             
    
   
                
                
                
                  
                                                             
    
   
                
                
                
                   
                                                            
    
                                                           
                                                           
                                                            
                                                           
  

                                                       
                                                                     
 

/** Position on the circle (0 = C/Am, clockwise). Enharmonic keys share a position. */
                                                         
                                             
 

                                                           
                                                              
 

/** The circle's primary tonic for a chroma (C, Db, D, Eb, E, F, F#, G, Ab, A, Bb, B; minors alike). */
                                                                         
                          
                                                          
                                                                        
                                                      
 

/** Move `steps` around the circle: + clockwise (sharpwards), − anticlockwise. */
                                                                        
                                                                                    
                                                                                     
 

/** The dominant (V) key, one step clockwise: C → G, Am → Em. */
                                                          
                                                                 
 

/** The subdominant (IV) key, one step anticlockwise: C → F, Am → Dm. */
                                                             
                                                                 
 

/** Relative minor of a major key (and vice versa), spelled by letter: F# → D#m. */
                                                          
                             
                                                               
                                                                
 

                                                           
                                                 
 

                                                           
                                                 
 

                                                          
                                                                              
 

export interface KeySignature {
  /** Sharps (+) or flats (−). Beyond ±7 the key is theoretical (double accidentals). */
  readonly alteration: number;
  readonly sharps: number;
  readonly flats: number;
  /** The altered notes in signature order, e.g. F# C# G#, or Bb Eb. */
  readonly accidentals: readonly string[];
}

                                                                 

/** Key signature from the tonic's spelling: Gb major has 6 flats, F# major 6 sharps. */
                                                             
                                
                                                                                                     
                    
                                                                                      
                                                                           
                                           
                                     
                                   
                                                
                                                  
                                                                       
   
          
               
                                    
                                    
                
    
 

import { chordSymbol } from "/Users/pookey/Documents/personal/Projects.nosync.noindex/guitar.nosync/app/src/theory/harmony.ts?split=27";
import { romanNumeral } from "/Users/pookey/Documents/personal/Projects.nosync.noindex/guitar.nosync/app/src/theory/harmony.ts?split=37";
import { nashvilleNumber } from "/Users/pookey/Documents/personal/Projects.nosync.noindex/guitar.nosync/app/src/theory/harmony.ts?split=38";
export { describe };
